/**
 * placeKindTag — country-qualified short label shown under a place
 * name so visitors from anywhere instantly understand the geography.
 *
 *   {kind:"state",  slug:"united-states/oregon"}       → "US STATE"
 *   {kind:"county", slug:"united-states/..."}          → "US COUNTY"
 *   {kind:"city",   slug:"india/kerala/kochi"}         → "INDIAN CITY"
 *   {kind:"district", slug:"india/kerala/ernakulam"}   → "INDIAN DISTRICT"
 *
 * `country` is optional: when passed we honor it; otherwise we
 * infer from the slug's first segment. Missing country returns
 * a bare kind label ("STATE", "CITY", …).
 */

import { Link } from "react-router-dom";
import { directorySlugFor } from "@/pages/KindDirectoryPage";

const COUNTRY_ADJECTIVE = {
  "united-states": "US",
  "usa":           "US",
  "us":            "US",
  "united states": "US",
  "india":         "INDIAN",
  "canada":        "CANADIAN",
  "united-kingdom":"UK",
  "uk":            "UK",
  "australia":     "AUSTRALIAN",
};

const KIND_LABEL = {
  country:  "COUNTRY",
  state:    "STATE",
  district: "DISTRICT",
  county:   "COUNTY",
  city:     "CITY",
  town:     "TOWN",
  village:  "VILLAGE",
};

function firstSlugSegment(slug) {
  return String(slug || "").split("/").filter(Boolean)[0] || "";
}

/** Return the full tag e.g. "US STATE" or "INDIAN DISTRICT". */
export function placeKindTag(kind, slug, country) {
  const k = KIND_LABEL[(kind || "").toLowerCase()];
  if (!k) return null;
  const rawCountry = String(country || firstSlugSegment(slug) || "").toLowerCase();
  const adj = COUNTRY_ADJECTIVE[rawCountry] || COUNTRY_ADJECTIVE[rawCountry.replace(/\s+/g, "-")];
  return adj ? `${adj} ${k}` : k;
}

/**
 * <PlaceKindTag /> — small monospace chip. Renders inline so it can
 * be dropped beneath a name or icon. Colour palette is per-kind so
 * the tag reinforces the same visual language as PlaceStamp.
 */
const KIND_STYLE = {
  country:  { color: "#0F172A", bg: "#F1F5F9" },
  state:    { color: "#B45309", bg: "#FFF7ED" },
  district: { color: "#7C3AED", bg: "#F5F3FF" },
  county:   { color: "#0E7490", bg: "#ECFEFF" },
  city:     { color: "#B91C1C", bg: "#FEF2F2" },
  town:     { color: "#1D4ED8", bg: "#EFF6FF" },
  village:  { color: "#15803D", bg: "#F0FDF4" },
};

export default function PlaceKindTag({ kind, slug, country, className = "", testid, asLink = false }) {
  const label = placeKindTag(kind, slug, country);
  if (!label) return null;
  const s = KIND_STYLE[(kind || "").toLowerCase()] || KIND_STYLE.city;
  const styles = {
    background: s.bg,
    color: s.color,
  };
  const cls = `inline-block text-[10px] font-mono uppercase tracking-widest rounded-full px-2 py-0.5 ${className}`;
  const dir = asLink ? directorySlugFor(kind, country || slug) : null;
  if (dir) {
    return (
      <Link
        to={`/directory/${dir}`}
        className={`${cls} hover:brightness-95 transition`}
        style={styles}
        data-testid={testid || `place-kind-tag-${(kind || "").toLowerCase()}`}
        title={`Browse all ${label}s`}
      >
        {label} →
      </Link>
    );
  }
  return (
    <span
      className={cls}
      style={styles}
      data-testid={testid || `place-kind-tag-${(kind || "").toLowerCase()}`}
    >
      {label}
    </span>
  );
}
